'use client'

import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

import type { Any } from '@api/orvalFetcher'
import { Kbd } from '@fp/ui-kit'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { emitToast, shortBusProps } from '@lib/emitToast'
import { PAYOUTS_ROUTE } from '@lib/payoutTerminology'

export const APP_HOTKEYS_LS_KEY = 'appHotkeys'

const DIGIT_TO_PATH: Record<string, { path: string; key: string }> = {
  Digit1: { path: '/accounts', key: '1' },
  Digit2: { path: '/users', key: '2' },
  Digit3: { path: PAYOUTS_ROUTE, key: '3' },
  Digit4: { path: '/referrals', key: '4' },
  Digit5: { path: '/transactions', key: '5' },
  Digit6: { path: '/email_templates', key: '6' },
  Digit7: { path: '/coupons', key: '7' },
  Digit8: { path: '/tasks', key: '8' },
  Digit9: { path: '/discount_tiers', key: '9' },
}

export const HOTKEY_BY_PATH: Record<string, string> = Object.fromEntries(
  Object.values(DIGIT_TO_PATH).map(({ path, key }) => [path, key]),
)

export const isTypingTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName

  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export function AppHotkeys() {
  const router = useRouter()
  const { valueLS: isEnabled } = useLocalStorage(APP_HOTKEYS_LS_KEY, false)

  useEffect(() => {
    if (!isEnabled) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return
      if (e.repeat || !e.ctrlKey) return

      // Use Ctrl+1..9 for navigation
      if (e.code && DIGIT_TO_PATH[e.code]) {
        e.preventDefault()
        const { path, key = e.code } = DIGIT_TO_PATH[e.code]!

        router.push(path)

        emitToast({
          ...shortBusProps,
          type: 'info',
          icon: <Kbd size='sm'>{key.toUpperCase()}</Kbd>,
          message: `Navigated to "${path}" (CTRL+${key.toUpperCase()}).`,
        })
      }
    }

    window.addEventListener('keydown', onKeyDown, { capture: true })

    return () => window.removeEventListener('keydown', onKeyDown, { capture: true } as Any)
  }, [router, isEnabled])

  return null
}
